import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ClinicConfigProvider } from "@/lib/clinicConfig";
import { AppearanceProvider } from "@/lib/AppearanceProvider";
import { WorkspaceProvider } from "@/lib/workspace";
import { Toaster } from "@/components/ui/sonner";
import DomainRouter from "@/components/DomainRouter";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import DashboardLayout from "@/pages/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import PatientProfile from "@/pages/PatientProfile";
import CalendarPage from "@/pages/CalendarPage";import Treatments from "@/pages/Treatments";
import Billing from "@/pages/Billing";
import BillingDashboard from "@/pages/BillingDashboard";
import BillingReports from "@/pages/BillingReports";
import Quotations from "@/pages/Quotations";
import Outstanding from "@/pages/Outstanding";
import AICommand from "@/pages/AICommand";
import WhatsAppAdmin from "@/pages/WhatsAppAdmin";
import WhatsAppSettings from "@/pages/WhatsAppSettings";
import WhatsAppDiagnostics from "@/pages/WhatsAppDiagnostics";
import ConnectMeta from "@/pages/ConnectMeta";
import AutomationTemplates from "@/pages/AutomationTemplates";
import AutomationMappingMatrix from "@/pages/AutomationMappingMatrix";
import WhatsAppInbox from "@/pages/WhatsAppInbox";
import ConnectWhatsApp from "@/pages/ConnectWhatsApp";
import Settings from "@/pages/Settings";
import SettingsLayout from "@/pages/settings/SettingsLayout";
import SettingsDashboard from "@/pages/settings/SettingsDashboard";
import * as S from "@/pages/settings/sections";
import Subscription from "@/pages/Subscription";
import TrialEnded from "@/pages/TrialEnded";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import PlatformAdmin from "@/pages/PlatformAdmin";
import Inventory from "@/pages/Inventory";
import TreatmentKits from "@/pages/TreatmentKits";
import InventoryReports from "@/pages/InventoryReports";
import InventoryAnalytics from "@/pages/InventoryAnalytics";
import PatientRecord from "@/pages/PatientRecord";
import ChairsideMode from "@/pages/ChairsideMode";
import DoctorAnalytics from "@/pages/DoctorAnalytics";
import AnalyticsHub, { AnalyticsOverview, AnalyticsComingSoon } from "@/pages/AnalyticsHub";
import DataMigration from "@/pages/DataMigration";
import ModuleManager from "@/pages/settings/ModuleManager";
import ModuleGate from "@/components/ModuleGate";
import KPIAudit from "@/pages/settings/KPIAudit";
import SchedulingDebug from "@/pages/settings/SchedulingDebug";
import DuplicatesReport from "@/pages/DuplicatesReport";
import DataImportSection from "@/pages/settings/DataImportSection";
import Letterheads from "@/pages/settings/Letterheads";
import LetterheadEditor from "@/pages/settings/LetterheadEditor";
import DoctorSignatures from "@/pages/settings/DoctorSignatures";
import ReceptionQueue from "@/pages/ReceptionQueue";
import BookingQRAdmin from "@/pages/BookingQRAdmin";
import CalendarIntegration from "@/pages/CalendarIntegration";
import AppearanceSettings from "@/pages/AppearanceSettings";
import QrBounce from "@/pages/QrBounce";
import PublicSharePage from "@/pages/PublicSharePage";
import PublicBooking from "@/pages/PublicBooking";
import PublicConsent from "@/pages/PublicConsent";
import PublicWarranty from "@/pages/PublicWarranty";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import DPDP from "@/pages/legal/DPDP";
import LimitedUse from "@/pages/legal/LimitedUse";
import BlogIndex from "@/pages/blog/BlogIndex";
import BlogPost from "@/pages/blog/BlogPost";
import ErrorBoundary from "@/components/ErrorBoundary";
import InvoicePreviewPage from "@/pages/InvoicePreviewPage";

function Private({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <>
      <DomainRouter />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/consent/:token" element={<PublicConsent />} />
      <Route path="/warranty/:token" element={<PublicWarranty />} />
      <Route path="/patient-record/:token" element={<PatientRecord />} />
      <Route path="/share/:token" element={<PublicSharePage />} />
      <Route path="/book/:slug" element={<PublicBooking />} />
      <Route path="/q/:slug" element={<QrBounce />} />
      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/dpdp" element={<DPDP />} />
      <Route path="/legal/limited-use" element={<LimitedUse />} />
      <Route path="/limited-use" element={<LimitedUse />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/invoices/:invoiceId/preview" element={<Private><InvoicePreviewPage /></Private>} />
      <Route path="/onboarding" element={<Private><Onboarding /></Private>} />
      <Route path="/platform" element={<Private><PlatformAdmin /></Private>} />
      <Route path="/trial-ended" element={<Private><TrialEnded /></Private>} />
      <Route path="/app/chairside/:id" element={<Private><ChairsideMode /></Private>} />
      <Route
        path="/app"
        element={<Private><DashboardLayout /></Private>}
      >
        <Route index element={<Dashboard />} />
        <Route path="patients" element={<Patients />} />
        <Route path="patients/:id" element={<PatientProfile />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="reception/queue" element={<ReceptionQueue />} />
        <Route path="treatments" element={<Treatments />} />
        <Route path="billing" element={<BillingDashboard />} />
        <Route path="billing/invoices" element={<Billing />} />
        <Route path="billing/quotations" element={<Quotations />} />
        <Route path="billing/outstanding" element={<Outstanding />} />
        <Route path="billing/reports" element={<BillingReports />} />
        <Route path="inventory" element={<ModuleGate module="inventory"><Inventory /></ModuleGate>} />
        <Route path="inventory/treatment-kits" element={<ModuleGate module="treatment_kits"><TreatmentKits /></ModuleGate>} />
        <Route path="inventory/reports" element={<ModuleGate module="inventory"><InventoryReports /></ModuleGate>} />
        <Route path="inventory/analytics" element={<ModuleGate module="inventory"><InventoryAnalytics /></ModuleGate>} />
        {/* Legacy top-level route — kept for old bookmarks. Suppliers + Purchase
            Orders modules were removed from the UI per the "keep it clinic-simple"
            refactor; backend routers /api/suppliers and /api/purchase-orders
            stay live so a future "advanced" toggle can re-enable them without
            data migration. */}
        <Route path="treatment-kits" element={<ModuleGate module="treatment_kits"><TreatmentKits /></ModuleGate>} />
        <Route path="ai" element={<ModuleGate module="ai_features"><AICommand /></ModuleGate>} />
        <Route path="whatsapp" element={<ModuleGate module="whatsapp"><WhatsAppAdmin /></ModuleGate>} />
        <Route path="whatsapp/inbox" element={<ModuleGate module="whatsapp"><WhatsAppInbox /></ModuleGate>} />
        <Route path="whatsapp/inbox/:phone" element={<ModuleGate module="whatsapp"><WhatsAppInbox /></ModuleGate>} />
        <Route path="whatsapp/settings" element={<ModuleGate module="whatsapp"><WhatsAppSettings /></ModuleGate>} />
        <Route path="whatsapp/diagnostics" element={<ModuleGate module="whatsapp"><WhatsAppDiagnostics /></ModuleGate>} />
        <Route path="whatsapp/connect-meta" element={<ModuleGate module="whatsapp"><ConnectMeta /></ModuleGate>} />
        <Route path="whatsapp/automations" element={<ModuleGate module="whatsapp"><AutomationTemplates /></ModuleGate>} />
        <Route path="whatsapp/automations/matrix" element={<ModuleGate module="whatsapp"><AutomationMappingMatrix /></ModuleGate>} />
        <Route path="whatsapp/connect" element={<ModuleGate module="whatsapp"><ConnectWhatsApp /></ModuleGate>} />
        <Route path="analytics/doctors" element={<DoctorAnalytics />} />
        {/* New Analytics hub — sectioned insights (Phase A) */}
        <Route path="analytics" element={<ModuleGate module="analytics"><AnalyticsHub /></ModuleGate>}>
          <Route index element={<AnalyticsOverview />} />
          <Route path="overview" element={<AnalyticsOverview />} />
          <Route path="revenue"      element={<AnalyticsComingSoon title="Revenue" description="Weekly / monthly revenue trends, top procedures, doctor split." />} />
          <Route path="patients"     element={<AnalyticsComingSoon title="Patients" description="New vs returning, demographics, recall pipeline." />} />
          <Route path="doctors"      element={<DoctorAnalytics />} />
          <Route path="appointments" element={<AnalyticsComingSoon title="Appointments" description="No-shows, utilisation, gap analysis per chair." />} />
          <Route path="inventory"    element={<AnalyticsComingSoon title="Inventory" description="Consumption, dead-stock, expiry risk, value trends." />} />
          <Route path="whatsapp"     element={<AnalyticsComingSoon title="WhatsApp" description="Delivery, open, reply rate + credit usage." />} />
          <Route path="reports"      element={<AnalyticsComingSoon title="Financial Reports" description="P&L, GST-ready summaries, doctor commissions." />} />
        </Route>
        <Route path="data-migration" element={<ModuleGate module="data_migration"><DataMigration /></ModuleGate>} />
        <Route path="patients/duplicates" element={<Navigate to="/app/settings/data/duplicates" replace />} />
        <Route path="subscription" element={<Subscription />} />

        {/* Letterhead field editor — needs full viewport, so it lives OUTSIDE
            the SettingsLayout shell (which would eat 320 px of horizontal
            space with its permanent sidebar). */}
        <Route path="settings/documents/letterheads/:id/edit" element={<LetterheadEditor />} />

        {/* Unified Settings shell — all settings live inside this layout, URL updates
            without navigating away. Legacy scrolling page removed from user routes. */}
        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<SettingsDashboard />} />
          {/* General */}
          <Route path="general/clinic-profile" element={<S.ClinicProfileSection />} />
          <Route path="general/workflow-mode"  element={<S.WorkflowModeSection />} />
          <Route path="general/branding"       element={<S.BrandingSection />} />
          <Route path="general/working-hours"  element={<S.WorkingHoursSection />} />
          <Route path="general/holidays"       element={<S.ComingSoon categorySlug="general" sectionSlug="holidays" />} />
          <Route path="general/timezone"       element={<S.ComingSoon categorySlug="general" sectionSlug="timezone" />} />
          <Route path="general/preferences"    element={<S.ComingSoon categorySlug="general" sectionSlug="preferences" />} />
          {/* Doctors & Staff */}
          <Route path="doctors/doctors"        element={<S.TeamSection filter="doctors" />} />
          <Route path="doctors/staff"          element={<S.TeamSection filter="staff" />} />
          <Route path="doctors/roles"          element={<S.ComingSoon categorySlug="doctors" sectionSlug="roles" />} />
          <Route path="doctors/permissions"    element={<S.ComingSoon categorySlug="doctors" sectionSlug="permissions" />} />
          <Route path="doctors/leave"          element={<S.ComingSoon categorySlug="doctors" sectionSlug="leave" />} />
          <Route path="doctors/availability"   element={<S.ComingSoon categorySlug="doctors" sectionSlug="availability" />} />
          {/* Appointments */}
          <Route path="appointments/settings"        element={<S.ComingSoon categorySlug="appointments" sectionSlug="settings" />} />
          <Route path="appointments/online-booking"  element={<BookingQRAdmin />} />
          <Route path="appointments/booking-qr"      element={<BookingQRAdmin />} />
          <Route path="appointments/calendar-rules"  element={<S.ComingSoon categorySlug="appointments" sectionSlug="calendar-rules" />} />
          <Route path="appointments/reminders"       element={<S.ComingSoon categorySlug="appointments" sectionSlug="reminders" />} />
          <Route path="appointments/cancellation"    element={<S.ComingSoon categorySlug="appointments" sectionSlug="cancellation" />} />
          <Route path="appointments/waiting-list"    element={<S.ComingSoon categorySlug="appointments" sectionSlug="waiting-list" />} />
          {/* Billing */}
          <Route path="billing/billing-preferences" element={<S.BillingPreferencesSection />} />
          <Route path="billing/invoice-settings"  element={<S.InvoiceSettingsSection />} />
          <Route path="billing/invoice-template"  element={<S.InvoiceTemplateSection />} />
          <Route path="billing/gst-tax"           element={<S.GstTaxSection />} />
          <Route path="billing/payment-methods"   element={<S.ComingSoon categorySlug="billing" sectionSlug="payment-methods" />} />
          <Route path="billing/wallet"            element={<S.ComingSoon categorySlug="billing" sectionSlug="wallet" />} />
          <Route path="billing/financial"         element={<S.ComingSoon categorySlug="billing" sectionSlug="financial" />} />
          {/* Clinical */}
          <Route path="clinical/procedures"       element={<S.ProceduresSection />} />
          <Route path="clinical/laboratories"     element={<S.LaboratoriesSection />} />
          <Route path="clinical/prescriptions"    element={<S.ComingSoon categorySlug="clinical" sectionSlug="prescriptions" />} />
          <Route path="clinical/consent"          element={<S.ComingSoon categorySlug="clinical" sectionSlug="consent" />} />
          <Route path="clinical/medical-history"  element={<S.ComingSoon categorySlug="clinical" sectionSlug="medical-history" />} />
          <Route path="clinical/dental-chart"     element={<S.ComingSoon categorySlug="clinical" sectionSlug="dental-chart" />} />
          {/* Documents & Templates (Phase 1: letterheads + doctor signatures) */}
          <Route path="documents/letterheads"       element={<Letterheads />} />
          <Route path="documents/doctor-signatures" element={<DoctorSignatures />} />
          <Route path="documents/document-templates" element={<S.ComingSoon categorySlug="documents" sectionSlug="document-templates" />} />
          {/* Inventory */}
          <Route path="inventory/suppliers"       element={<S.ComingSoon categorySlug="inventory" sectionSlug="suppliers" />} />
          <Route path="inventory/low-stock"       element={<S.ComingSoon categorySlug="inventory" sectionSlug="low-stock" />} />
          <Route path="inventory/auto-deduct"     element={<S.ComingSoon categorySlug="inventory" sectionSlug="auto-deduct" />} />
          <Route path="inventory/units"           element={<S.ComingSoon categorySlug="inventory" sectionSlug="units" />} />
          <Route path="inventory/expiry"          element={<S.ComingSoon categorySlug="inventory" sectionSlug="expiry" />} />
          {/* Communication */}
          <Route path="communication/email"                   element={<S.ComingSoon categorySlug="communication" sectionSlug="email" />} />
          <Route path="communication/notification-templates"  element={<S.ComingSoon categorySlug="communication" sectionSlug="notification-templates" />} />
          <Route path="communication/reminder-templates"      element={<S.ComingSoon categorySlug="communication" sectionSlug="reminder-templates" />} />
          {/* Integrations */}
          <Route path="integrations/google-calendar" element={<CalendarIntegration />} />
          <Route path="integrations/payment-gateway" element={<S.ComingSoon categorySlug="integrations" sectionSlug="payment-gateway" />} />
          <Route path="integrations/ai-providers"    element={<S.ComingSoon categorySlug="integrations" sectionSlug="ai-providers" />} />
          <Route path="integrations/api-keys"        element={<S.ComingSoon categorySlug="integrations" sectionSlug="api-keys" />} />
          <Route path="integrations/webhooks"        element={<S.ComingSoon categorySlug="integrations" sectionSlug="webhooks" />} />
          {/* Data */}
          <Route path="data/import"      element={<DataImportSection />} />
          <Route path="data/export"      element={<S.ComingSoon categorySlug="data" sectionSlug="export" />} />
          <Route path="data/duplicates"  element={<DuplicatesReport />} />
          <Route path="data/backup"      element={<S.ComingSoon categorySlug="data" sectionSlug="backup" />} />
          <Route path="data/audit-logs"  element={<S.ComingSoon categorySlug="data" sectionSlug="audit-logs" />} />
          {/* Appearance */}
          <Route path="appearance/theme"          element={<AppearanceSettings />} />
          <Route path="appearance/colors"         element={<AppearanceSettings />} />
          <Route path="appearance/typography"     element={<AppearanceSettings />} />
          <Route path="appearance/sidebar"        element={<AppearanceSettings />} />
          <Route path="appearance/density"        element={<AppearanceSettings />} />
          <Route path="appearance/accessibility"  element={<AppearanceSettings />} />
          {/* Subscription */}
          <Route path="subscription/current-plan"     element={<Subscription />} />
          <Route path="subscription/usage"            element={<Subscription />} />
          <Route path="subscription/storage"          element={<S.ComingSoon categorySlug="subscription" sectionSlug="storage" />} />
          <Route path="subscription/billing-history"  element={<S.ComingSoon categorySlug="subscription" sectionSlug="billing-history" />} />
          {/* Workspace — Module Manager + Data Migration toggle */}
          <Route path="workspace/modules" element={<ModuleManager />} />
          <Route path="workspace/kpi-audit" element={<KPIAudit />} />
          <Route path="workspace/scheduling-debug" element={<SchedulingDebug />} />
          {/* Advanced */}
          <Route path="advanced/developer"      element={<S.ComingSoon categorySlug="advanced" sectionSlug="developer" />} />
          <Route path="advanced/feature-flags"  element={<S.ComingSoon categorySlug="advanced" sectionSlug="feature-flags" />} />
          <Route path="advanced/system"         element={<S.ComingSoon categorySlug="advanced" sectionSlug="system" />} />
          <Route path="advanced/danger"         element={<S.ComingSoon categorySlug="advanced" sectionSlug="danger" />} />
        </Route>

        {/* Dev-only fallback to the legacy page (never linked from the UI). */}
        <Route path="__legacy_settings" element={<Settings />} />
      </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <HelmetProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppearanceProvider>
                <ClinicConfigProvider>
                  <WorkspaceProvider>
                    <AppRoutes />
                    <Toaster position="top-right" />
                  </WorkspaceProvider>
                </ClinicConfigProvider>
              </AppearanceProvider>
            </AuthProvider>
          </BrowserRouter>
        </HelmetProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
